import {
  adminAddService,
  adminRemoveService,
  disableGlobalService,
  disableWorkplaceService,
  enableGlobalService,
  enableWorkplaceService,
  getCommandHistory,
} from 'services/service/admin.service';
import { getServices } from 'services/service/service.service';
import { restartService, startService, stopService } from 'services/service/supervisor.service';
import type {
  ServiceToggleAction,
  ServiceTogglePayload,
  ToggleTarget,
} from '../types/Deployments.types';
import type {
  CommandHistoryParams,
  RawService,
  ServiceAction,
  ServiceRecord,
} from '../types/ServiceManager.types';
import { canBeModified, mapServiceRecord } from '../utils/ServiceManager.utils';

/** Result shape returned by {@link fetchServicesOp}. */
export interface FetchServicesResult {
  /** Services owned by the caller's workplace. */
  services: ServiceRecord[];
  /**
   * Globally-scoped services (AuthService, the CW parsers, and so on), rendered
   * in their own section. One shared instance per supervisor rather than one
   * per deployment.
   */
  globalServices: ServiceRecord[];
  /**
   * Rows the supervisor holds but PM2 has no process for. These are candidates
   * for the Add Service modal.
   *
   * A service whose ENABLE FLAG is off does not appear here, because it has no
   * registry row at all. Those live only in the catalog
   * (`GET /admin/workplaces`) and are turned on from the Deployments tab.
   */
  removedServices: RawService[];
}

/**
 * Fetch services from the API and split them into the caller's workplace bucket
 * and the global bucket.
 *
 * Scoping is the server's job: the Fusion proxy filters the listing by the
 * caller's `workplace_id` read from the verified token, always includes
 * globally-scoped rows, and tags every row with `scope`. Pass `{ all: true }`
 * to see every deployment's services instead.
 *
 * Rows without a `scope` come from a supervisor that predates the field; they
 * fall into the workplace bucket, which is where they used to render.
 */
export const fetchServicesOp = async ({ all = false } = {}): Promise<FetchServicesResult> => {
  const res = await getServices({ all });

  const apiServices: RawService[] = res?.data?.services || [];

  const validServices = apiServices.filter(
    (service) => service?.pm_id != null && service?.state && service.state !== 'missing',
  );

  const missingServices = apiServices.filter((service) => service?.state === 'missing');

  const mapped = validServices.map(mapServiceRecord);

  return {
    services: mapped.filter((s) => s.scope !== 'global'),
    globalServices: mapped.filter((s) => s.scope === 'global'),
    removedServices: missingServices,
  };
};

/**
 * Perform a real-time action (start, stop, restart) on a service.
 *
 * These are transient run commands: they act on a process the supervisor is
 * already configured to run. They cannot bring up a service whose enable flag
 * is off, because the supervisor's next tick reaps anything running that its
 * configuration says should not be. Use {@link setServiceEnabledOp} for that.
 *
 * @param service - The service to act on.
 * @param action - The action to perform.
 * @param reason - Reason string recorded in command history.
 */
export const performServiceActionOp = async (
  service: ServiceRecord,
  action: ServiceAction,
  reason: string = '',
): Promise<void> => {
  if (!service) {
    console.warn(
      '[ServiceManager] performServiceActionOp: service not provided for action:',
      action,
    );
    return;
  }

  if (!canBeModified(service)) {
    return;
  }

  switch (action) {
    case 'start':
      await startService(service.name, reason);
      break;
    case 'stop':
      await stopService(service.name, reason);
      break;
    case 'restart':
      await restartService(service.name, reason);
      break;
    default:
      console.warn('[ServiceManager] performServiceActionOp: unknown action:', action);
  }
};

// Service enablement ---------------------------------------------------------

/**
 * Outcome of an enable/disable. A discriminated union rather than a throw,
 * because the caller has to tell three cases apart and render each differently:
 * it worked, the supervisor refused, or this Fusion backend does not serve the
 * route at all.
 */
export type SetServiceEnabledResult =
  | { kind: 'applied'; payload: ServiceTogglePayload }
  | {
      kind: 'failed';
      /** HTTP status, when the request reached a server. */
      status?: number;
      /** Server's own sentence where there is one, else a generic fallback. */
      message: string;
      /**
       * The route is not served by this Fusion backend (404 on the global
       * toggle, which the proxy has not shipped). An infrastructure gap, not a
       * failure of the operator's action, so it reads as a calm state.
       */
      unsupported?: boolean;
    };

/** Pull the server's own message off an axios-shaped rejection. */
const serverMessage = (err: unknown): string | undefined => {
  const body = (err as { response?: { data?: { message?: unknown } } })?.response?.data;
  return typeof body?.message === 'string' && body.message.trim() ? body.message : undefined;
};

const errorStatus = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;

/**
 * Turn a service on or off for one deployment, or globally.
 *
 * This is the control that governs instancing. A workplace-scoped service is
 * instanced once per deployment and its flag lives in
 * `fusion_supervisor_deployment.options`; a global-scoped service runs as one
 * shared instance and its flag lives in `fusion_supervisor.global_options`.
 *
 * Both are hot: the supervisor writes the flag, reloads its config and
 * reconciles PM2 without a restart. Enabling spawns the process on the next
 * tick; disabling stops it inside the request.
 *
 * The 200 body carries `effective` re-read from the reloaded config rather than
 * echoed from the request, so a flag that was written but not reconciled comes
 * back with the OLD value. Callers must bind their switch to that read-back.
 */
export const setServiceEnabledOp = async (
  target: ToggleTarget,
  enabled: boolean,
  reason: string = '',
): Promise<SetServiceEnabledResult> => {
  const action: ServiceToggleAction = enabled ? 'enable' : 'disable';
  try {
    const res =
      target.scope === 'global'
        ? await (enabled ? enableGlobalService : disableGlobalService)(target.name, reason)
        : await (enabled ? enableWorkplaceService : disableWorkplaceService)(
            target.wpIndex,
            target.name,
            reason,
          );

    // The Fusion proxy passes the supervisor's body through under `data`.
    const payload = (res as { data?: ServiceTogglePayload })?.data;
    if (!payload || typeof payload.effective !== 'boolean') {
      return {
        kind: 'failed',
        message: `The supervisor answered without an effective state, so we cannot confirm the ${action} took.`,
      };
    }
    return { kind: 'applied', payload };
  } catch (err) {
    const status = errorStatus(err);
    // The Fusion proxy has no route for the global toggle yet. Distinguish it
    // so the UI can say "this backend has not shipped it" rather than blaming
    // the operator, and so the feature lights up on the backend change alone.
    if (status === 404 && target.scope === 'global') {
      return {
        kind: 'failed',
        status,
        unsupported: true,
        message:
          'This Fusion backend does not expose the global service toggle yet. ' +
          'Global services can be switched from the internal admin console until the proxy route ships.',
      };
    }
    return {
      kind: 'failed',
      status,
      message:
        serverMessage(err) ??
        (err instanceof Error ? err.message : `Could not ${action} ${target.name}.`),
    };
  }
};

/**
 * Add a previously removed service back by PM2 name. The Fusion proxy supplies
 * the owning `workplaceId` from the caller's token.
 *
 * @param serviceName - The PM2 name of the service to add.
 */
export const addServiceOp = async (serviceName: string): Promise<void> => {
  await adminAddService(serviceName);
};

/**
 * Remove a service by its process-manager id.
 *
 * @param serviceId - The numeric PM2 id of the service record.
 */
export const removeServiceOp = async (serviceId: number): Promise<void> => {
  await adminRemoveService(serviceId);
};

/** Result shape returned by {@link fetchCommandHistoryOp}. */
export interface CommandHistoryResult {
  rows: Array<{
    id: number;
    service: string;
    command: string;
    user_id: number;
    user_email?: string;
    ts?: string;
    reason?: string;
    success: boolean;
  }>;
  total: number;
}

/**
 * Fetch paginated command history with optional filters.
 *
 * @param params - Pagination and filter parameters.
 * @returns Matching rows and total record count.
 */
export const fetchCommandHistoryOp = async (
  params: CommandHistoryParams,
): Promise<CommandHistoryResult> => {
  const res = await getCommandHistory(params);

  return {
    rows: res?.data?.rows || [],
    total: res?.data?.total ?? 0,
  };
};
